<template>
  <div class="card shadow" :class="type === 'dark' ? 'bg-default' : ''">
    <div
      class="card-header border-0"
      :class="type === 'dark' ? 'bg-transparent' : ''"
    >
      <div class="row align-items-center">
        <div class="col">
          <h3 class="mb-0" :class="type === 'dark' ? 'text-white' : ''">
            {{ title }}
          </h3>
        </div>
        <div class="col text-right">
          <span v-if="savingOrder" class="text-muted mr-3"><i class="fa fa-spinner fa-spin"></i> Saving order...</span>
          <base-button type="success" size="sm" @click="showCreateModal()" icon="fa fa-plus">Add instrument</base-button>
        </div>
      </div>

      <base-alert type="success" class="mt-3" v-if="successMessage">
        Instrument has been successfully saved.
      </base-alert>
      <base-alert type="success" class="mt-3" v-if="orderSuccessMessage">
        Order has been successfully updated.
      </base-alert>
      <base-alert type="danger" class="mt-3" v-if="draggableError">
        Drag and drop functionality failed to load. Please refresh the page or try a different browser.
      </base-alert>
    </div>

    <div class="table-responsive">
      <table
        class="table align-items-center table-flush"
        :class="type === 'dark' ? 'table-dark' : ''"
      >
        <thead :class="type === 'dark' ? 'thead-dark' : 'thead-light'">
          <tr>
            <th>Order</th>
            <th>Instrument</th>
            <th>Status</th>
            <th>Available</th>
            <th>Message</th>
            <th>Updated</th>
            <th></th>
          </tr>
        </thead>
        <draggable
          v-model="draggableItems"
          tag="tbody"
          class="list"
          item-key="id"
          @start="drag=true"
          @end="drag=false"
          @change="saveOrder"
          handle=".drag-handle"
        >
          <template #item="{ element: item }">
            <tr :key="item.id">
              <td class="drag-handle" style="cursor: move; width: 50px;">
                <i class="fas fa-grip-vertical"></i>
              </td>
              <th scope="row">
                <div class="media align-items-center">
                  <div class="media-body">
                    <span class="name mb-0 text-sm">{{ item.name }}</span>
                  </div>
                </div>
              </th>
              <td class="budget text-center">
                <div class="media align-items-center ml-3">
                  <badge class="badge-dot mr-4" :type="item.color">
                    <i :class="`bg-${item.color}`"></i>
                  </badge>
                </div>
              </td>
              <td>
                <div class="media align-items-center ml-3">
                  <badge class="mr-4" :type="itemType(item)">
                    <span class="status">{{ itemTypeStr(item) }}</span>
                  </badge>
                </div>
              </td>

              <td>
                {{ item.message }}
              </td>

              <td>
                {{ dateFormat(item.updatedAt) }}
              </td>

              <td class="text-right">
                <button v-if="item.enabled" class="btn btn-danger btn-sm"  @click="disabledItem(item)">Disable</button>
                <button v-if="!item.enabled" class="btn btn-success btn-sm" @click="enabledItem(item)">Enable</button>
                <button class="btn btn-icon btn-primary btn-sm" @click="showEditModal(item)">
                  <span class="btn-inner--icon"><i class="fa fa-edit"></i></span>
                  <span class="btn-inner--text">Edit</span>
                </button>
              </td>
            </tr>
          </template>
        </draggable>
      </table>
    </div>

     <modal v-model:show="showModal">
      <template v-slot:header>
          <h5 class="modal-title" v-if="item.id">Edit instrument</h5>
          <h5 class="modal-title" v-if="!item.id">Add instrument</h5>
      </template>
      <form id=item-form role="form" @submit.prevent="handleForm(!v$.$invalid)" novalidate>
        <div>
          <label for="item_name" class="form-label">Instrument</label>
          <base-input
              id="item_name"
              placeholder="Instrument"
              v-model="v$.item.name.$model"
              :valid="isValid(v$.item.name)"
              focused
              required></base-input>
        </div>

        <label class="form-label">Instrument Status</label>
        <div class="row">
          <div class="col-md-4">
              <div class="custom-control custom-radio">
                <input
                  id="item_color_success"
                  name="item_color"
                  class="custom-control-input"
                  type="radio"
                  value="success"
                  v-model="v$.item.color.$model"
                />
                <label for="item_color_success" class="custom-control-label text-success">
                  Ok
                </label>
              </div>
          </div>
          <div class="col-md-4">
              <div class="custom-control custom-radio">
                <input
                  id="item_color_warning"
                  name="item_color"
                  class="custom-control-input"
                  type="radio"
                  value="warning"
                  v-model="v$.item.color.$model"
                />
                <label for="item_color_warning" class="custom-control-label text-warning">
                  Incident being resolved
                </label>
              </div>
          </div>
          <div class="col-md-4">
              <div class="custom-control custom-radio">
                <input
                  id="item_color_danger"
                  name="item_color"
                  class="custom-control-input"
                  type="radio"
                  value="danger"
                  v-model="v$.item.color.$model"
                />
                <label for="item_color_danger" class="custom-control-label text-danger">
                  Incident
                </label>
              </div>
          </div>
        </div>
        <div class="mt-3">
          <label for="item_message" class="form-label">Message</label>
          <textarea class="form-control"
              id="item_message"
              v-model="v$.item.message.$model"
              rows="3"
              :class="[{ 'is-valid': isValid(v$.item.message) },{ 'is-invalid': isValid(v$.item.message) === false }]"
              required></textarea>
        </div>
        <div class="mt-3">
          <label for="item_message" class="form-label">Available</label>
          <div>
            <base-switch v-model="v$.item.enabled.$model"></base-switch>
          </div>
        </div>
      </form>
      <template v-slot:footer>
          <base-button type="secondary" @click="showModal = false">Cancel</base-button>
          <base-button nativeType="submit" type="primary" form="item-form">Save</base-button>
      </template>
    </modal>
  </div>
</template>

<script>
import moment from 'moment';
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import draggable from 'vuedraggable'

export default {
  name: "draggable-devices-table",
  components: {
    draggable
  },
  setup: () => ({ v$: useVuelidate() }),
  props: {
    type: {
      type: String,
    },
    title: String,
  },
  created() {
		this.reloadData();
	},
  data() {
    return {
      items: [],
      draggableItems: [],
      showModal: false,
      item: {},
      isSubmitted: false,
      successMessage: false,
      orderSuccessMessage: false,
      savingOrder: false,
      draggableError: false,
      drag: false
    };
  },
  validations() {
    return {
        item: {
            name: {required},
            color: {required},
            message: {required},
            enabled: {}
        }
    }
  },
  methods:{
    reloadData() {
      this.$resources.devices
        .list()
        .then(response => {
          this.items = response.data;
          this.draggableItems = [...response.data];
        })
        .catch(error => {
          console.error('Error loading devices:', error);
          this.draggableError = true;
        });
    },
    itemType(item) {
      return item.enabled ? 'success' : 'danger';
    } ,
    itemTypeStr(item) {
      return item.enabled ? 'On' : 'Off';
    },
    dateFormat(date) {
      return moment(String(date)).format('L LTS');
    },
    showEditModal(item) {
      this.isSubmitted = false;
      this.showModal = true;
      this.item = {...item};
    },
    showCreateModal() {
      this.isSubmitted = false;
      this.showModal = true;
      this.item = {
        enabled: true
      };
    },
    isValid(item){
        if (!this.isSubmitted){
          return null;
        }

        return !item.$invalid;
    },
    handleForm(isValid) {
      this.isSubmitted = true;

      if (!isValid) return;

      this.saveItem(this.item, true);
    },
    saveItem(item, showAlert){
      if (item.id) {
          this.$resources.devices.update(item.id, item).then( (data) => {
              this.reloadData();

              showAlert && (this.successMessage = true);
              this.items[this.findIndexById(item.id)] = data;

              this.showModal = false;
              this.item = {};

          });
      } else {
          this.$resources.devices.create(item).then( (data) => {
              this.items.unshift(data);
              this.draggableItems.unshift(data);
              showAlert && (this.successMessage = true);

              this.showModal = false;
              this.item = {};
          });
      }
    },
    enabledItem(item) {
        item.enabled = true;
        this.saveItem(item, false);
    },
    disabledItem(item) {
        item.enabled = false;
        this.saveItem(item, false);
    },
    findIndexById(id) {
      let index = -1;
      for (let i = 0; i < this.items.length; i++) {
          if (this.items[i].id === id) {
              index = i;
              break;
          }
      }
      return index;
    } ,
    saveOrder() {
      if (this.savingOrder) return;
      this.savingOrder = true;
      this.orderSuccessMessage = false;

      const newOrder = this.draggableItems.map(item => item.id);

      this.$resources.devices
        .updateOrder({ order: newOrder })
        .then(() => {
          this.orderSuccessMessage = true;
          setTimeout(() => { this.orderSuccessMessage = false; }, 2000);
          this.reloadData();
        })
        .catch(error => {
          console.error('Error saving order:', error.response?.data || error);
          this.reloadData();
        })
        .finally(() => {
          this.savingOrder = false;
        });
    }
  }
};
</script>

<style>
.drag-handle {
  cursor: move;
  color: #999;
}

.drag-handle:hover {
  color: #666;
}

.table-flush td {
  border-bottom: 1px solid #e9ecef;
}
</style>